// FleetMap: Explore's page. The range's AI work as one board (the rings, or
// FlowBoard), or the work by one kind of thing it reached (FleetInventory),
// one part at a time: the bar at the foot names each part and switches to
// it, so the page never scrolls from one kind of reading into another. The
// search is held at the top whichever part is up. The board fills the page
// and stretches with it: every column, card and strand is laid out for the
// room the page has. Everything on the page reads what the one search
// reaches, and a card or a row picked narrows that search.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FlowBoard, { STEPS, useAgentHues } from "./FlowBoard.jsx";
import ScrollArea from "./ScrollArea.jsx";
import FleetInventory, { KINDS } from "./FleetInventory.jsx";
import WelcomeFrame from "./welcome/WelcomeFrame.jsx";
import { ViewSwitch } from "./welcome/parts.jsx";
import { WELCOME_VIEWS, keepWelcomeView, readWelcomeView } from "./welcome/views.js";
import { FINDER_BOX_H, withoutClause } from "./SearchPalette.jsx";
import { useQuery } from "../lib/dataClient.js";
import { useSessionFacts } from "../lib/sessionFacts.js";
import { appendClause, holdsClause, setWorkspaceSearch, useReviewReach, useSearchScope, useWorkspacePicks } from "../lib/workspaceSearch.js";
import { rangeBoundsMs } from "../lib/analyticsClient.js";
import { toApiRange } from "../lib/shell.js";
import { pipelineFilterText } from "../lib/queryPipeline.js";
import { usePaneFoot } from "../lib/paneFoot.js";

const MARGIN = 16;
// The search box held at the top of the page, on the page's ground to
// HEAD_GAP under it, so what scrolls goes under it: at the top of the page
// that gap is room the board leaves above its captions, so the ground hides
// nothing there. What scrolls the page to a part stops it under the box.
const HEAD_GAP = 10;
const HEAD = MARGIN + FINDER_BOX_H + HEAD_GAP;
// The page scrolls as every mode's sidebar does, its bar laid over it; the
// parts strip as the tab strips do, sideways, its bar shown while it moves.
const PAGE_SCROLL = Object.freeze({ overflow: { x: "hidden" } });
const PARTS_SCROLL = Object.freeze({ overflow: { x: "scroll", y: "hidden" }, scrollbars: { autoHide: "scroll", autoHideDelay: 400 } });

// The board's middle column says who ran the work: the groups they are in,
// or the people, as this browser last chose (people at first).
const MIDDLE = ["group", "person"];
const MIDDLE_KEY = "origin-welcome-middle-v1";
const SWAPS = [null, MIDDLE.map((k) => STEPS[k]), null];
function readMiddle() {
  try {
    const saved = globalThis.localStorage?.getItem(MIDDLE_KEY);
    return MIDDLE.includes(saved) ? saved : "person";
  } catch {
    return "person";
  }
}

// The page's parts, a hairline cell each, as the pane's own foot cells are
// drawn, the part shown lit as the app's rail lights its mode (the pressed
// tint and a green bar, here along the cell's foot): in the pane's foot band
// where there is one, else in a band of the page's own. Too many for the
// strip's width, the part shown stays in sight, with room for a part either
// side. A click shows a part.
const BAND_H = 32;
// The kinds, each a part after the overview; the groups only for a tenant
// whose directory gives someone a department.
const KINDS_NO_GROUPS = KINDS.filter((k) => k.id !== "groups");
const OVERVIEW = "overview";
const partsOf = (kinds) => [{ id: OVERVIEW, label: "Overview" }, ...kinds];
// The part shown is kept for the browser tab, so leaving Explore and coming
// back finds it where it was.
const PART_KEY = "origin-explore-part-v1";
function readPart() {
  try {
    return globalThis.sessionStorage?.getItem(PART_KEY) || OVERVIEW;
  } catch {
    return OVERVIEW;
  }
}
function keepPart(id) {
  try {
    globalThis.sessionStorage?.setItem(PART_KEY, id);
  } catch {
    // Not kept past this page: the part still stands while it is open.
  }
}
const noSectionRef = () => {};
// The directory changes slowly: whether it has groups is read again at most
// this often.
const DIRECTORY_STALE_MS = 10 * 60_000;
const PART_CELL = "flex shrink-0 items-center whitespace-nowrap border-l border-[color:var(--line-soft)] px-[10px] leading-[16px] transition-colors";
function PartsBar({ parts, active, onGo }) {
  const strip = useRef(null);
  const setStrip = useCallback((instance) => { strip.current = instance ? instance.elements().viewport : null; }, []);
  useEffect(() => {
    const view = strip.current;
    const cell = view?.querySelector(`[data-fleet-nav-part="${active}"]`);
    if (!view || !cell || view.scrollWidth <= view.clientWidth) return;
    const v = view.getBoundingClientRect();
    const c = cell.getBoundingClientRect();
    const x0 = c.left - v.left + view.scrollLeft;
    const room = Math.min(80, Math.max(0, (view.clientWidth - c.width) / 2));
    const left = x0 - room < view.scrollLeft ? x0 - room
      : x0 + c.width + room > view.scrollLeft + view.clientWidth ? x0 + c.width + room - view.clientWidth : null;
    if (left != null) view.scrollTo?.({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);
  return (
    <ScrollArea instanceRef={setStrip} options={PARTS_SCROLL} observeMutations={false} className="min-w-0 border-r border-[color:var(--line-soft)]" contentClassName="h-full">
      <nav className="flex h-full items-stretch text-[11px]" aria-label="Parts of the overview" data-fleet-nav="">
        {parts.map((part) => {
          const on = active === part.id;
          return (
            <button
              key={part.id}
              type="button"
              onClick={() => onGo(part.id)}
              aria-current={on ? "true" : undefined}
              className={`${PART_CELL} ${on ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--ink)] shadow-[inset_0_-2px_0_var(--green)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`}
              data-fleet-nav-part={part.id}
            >
              {part.label}
            </button>
          );
        })}
      </nav>
    </ScrollArea>
  );
}

export default function FleetMap({ range = "1M", onStartSearch, startLabel, startHint }) {
  const scope = useSearchScope(range);
  const metrics = useQuery("getOverviewMetrics", { scopePath: scope.scopePath, range, query: scope.steer, byTrace: true }, { enabled: scope.ready });
  const read = useSessionFacts(scope, range);
  // What the board last read stays up, dimmed, while the next reading is
  // under way, so narrowing the search moves the board rather than blanking
  // it; before any reading there is the board's placeholder.
  const last = useRef({ facts: null, metrics: null });
  if (read.status === "done") last.current.facts = read;
  if (metrics.data) last.current.metrics = metrics;
  const facts = read.status === "loading" && last.current.facts ? { ...last.current.facts, stale: true } : read;
  const shownMetrics = metrics.data || !last.current.metrics ? metrics : { ...last.current.metrics, stale: true };
  // Drawn from the newest of what the search reaches, the board counts only
  // those: where review left every one, for the search box to count and the
  // review cards to say the whole of (a card of 2 drawn here, 7 in all, is
  // 7 drilled down to).
  const flagged = useReviewReach(range, scope.q, { enabled: !!facts.capped && scope.ready });
  const reached = useMemo(() => {
    if (!flagged) return null;
    const total = Number(metrics.data?.sessions);
    const some = flagged.met + flagged.not_verified + flagged.not_met + flagged.set_aside;
    return { ...flagged, none: Number.isFinite(total) ? Math.max(0, total - some) : null };
  }, [flagged, metrics.data]);
  // Groups only where the directory gives someone a department (until that is
  // read, none): elsewhere the board, the sections and the finder go by people.
  const directory = useQuery("getDirectoryGroups", {}, { staleMs: DIRECTORY_STALE_MS });
  const groups = !!directory.data?.any;
  const kinds = groups ? KINDS : KINDS_NO_GROUPS;
  const partList = useMemo(() => partsOf(kinds), [kinds]);
  // Which agents ran the work, the groups or the people who ran it, and where
  // review left it.
  const [middle, setMiddle] = useState(readMiddle);
  const shownMiddle = groups ? middle : "person";
  const steps = useMemo(() => [STEPS.agent, STEPS[shownMiddle], STEPS.review], [shownMiddle]);
  const onSwap = useCallback((_ci, def) => {
    const key = MIDDLE.find((k) => STEPS[k] === def);
    if (!key) return;
    setMiddle(key);
    try {
      globalThis.localStorage?.setItem(MIDDLE_KEY, key);
    } catch {
      // Not kept past this page: the choice still stands while it is open.
    }
  }, []);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  const bounds = useMemo(() => {
    const { fromMs, toMs } = rangeBoundsMs(typeof range === "string" ? toApiRange(range) : range);
    return { from: fromMs, to: Math.min(toMs, Date.now()) };
  }, [rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // What scrolls: the part shown; the overlay scrollbars' viewport, marked
  // for what looks for the page's scroll.
  const viewRef = useRef(null);
  const [scroller, setScroller] = useState(null);
  const setView = useCallback((instance) => {
    const el = instance ? instance.elements().viewport : null;
    el?.setAttribute?.("data-fleet-scroll", "");
    viewRef.current = el;
    setScroller(el);
  }, []);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = scroller;
    if (!el) return undefined;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [scroller]);
  // A card or a row picked narrows the search to it, and everything on the
  // page and in the sidebar's list follows; the one it is narrowed to
  // already, picked, widens it back out.
  const onPick = useCallback((clause) => setWorkspaceSearch((t) => (holdsClause(t, clause) ? withoutClause(t, clause) : appendClause(t, clause))), []);
  // The part shown: the overview, or one kind. A kind this tenant does not
  // show (groups, with no departments in the directory) is the overview.
  const [part, setPart] = useState(readPart);
  const active = partList.some((p) => p.id === part) ? part : OVERVIEW;
  const goTo = useCallback((id) => {
    setPart(id);
    keepPart(id);
    viewRef.current?.scrollTo?.({ top: 0 });
  }, []);
  // A kind's row drilled down to (or back out of) narrows the search, and
  // the overview comes up, to see what it narrowed to.
  const onDrill = useCallback((clause) => {
    onPick(clause);
    goTo(OVERVIEW);
  }, [onPick, goTo]);
  // The finder hands over the search as it stands at the keypress (the
  // scope trails the typing), with the sessions picked into it; once started
  // from, the picks are let go.
  const [picks, setPicks] = useWorkspacePicks();
  // The opener as it stands, so the search box's start does not change (and
  // the box redraw) each time the pane does.
  const startRef = useRef(onStartSearch);
  startRef.current = onStartSearch;
  const onStart = useCallback((text) => {
    const q = pipelineFilterText(text ?? scope.q).trim();
    if (!q && !picks.length) return;
    startRef.current?.(q, picks);
    setPicks([]);
  }, [scope.q, picks, setPicks]);

  // The board takes the page while the overview is up. The parts go in the
  // pane's foot, or a band of the page's own without one.
  const foot = usePaneFoot();
  const [searchHost, setSearchHost] = useState(null);
  const boardW = Math.max(480, size.w - 2 * MARGIN);
  const boardH = Math.max(560, size.h - 2 * MARGIN);
  const parts = <PartsBar parts={partList} active={active} onGo={goTo} />;
  // The agents' colours, the board's and the Agents section's alike.
  const hues = useAgentHues(facts);
  const onOverview = active === OVERVIEW;
  const shownKinds = useMemo(() => kinds.filter((k) => k.id === active), [kinds, active]);
  // How the first screen shows the work: the flow board, or another of the
  // views tried beside it, named under it.
  const [viewId, setViewId] = useState(readWelcomeView);
  const onView = useCallback((id) => {
    setViewId(id);
    keepWelcomeView(id);
  }, []);
  const view = WELCOME_VIEWS.find((v) => v.id === viewId) || WELCOME_VIEWS[0];
  const views = <ViewSwitch views={WELCOME_VIEWS} view={view.id} onView={onView} />;
  return (
    <div className="flex h-full min-h-0 flex-col" data-workspace-overview="">
      <ScrollArea instanceRef={setView} options={PAGE_SCROLL} observeMutations={false} className="min-h-0 flex-1 bg-[color:var(--workspace-bg)]" contentClassName="isolate">
        {size.w ? (
          <>
            <div className="sticky top-0 z-[40] h-0" data-board-search-dock="">
              <div className="absolute inset-x-0 top-0 bg-[color:var(--workspace-bg)]" style={{ height: HEAD }} aria-hidden="true" data-board-search-ground="" />
              <div ref={setSearchHost} className="absolute" style={{ left: MARGIN, top: MARGIN, width: boardW }} data-board-search-host="" />
            </div>
            {/* Under a kind the board is kept but not shown: its search box
                stands in the dock above whichever part is up. */}
            <div className="relative" style={{ height: MARGIN + boardH + MARGIN, display: onOverview ? undefined : "none" }} data-fleet-board="">
              <div className="absolute" style={{ left: MARGIN, top: MARGIN }}>
                {view.Body ? (
                  <WelcomeFrame
                    view={view}
                    facts={facts}
                    width={boardW}
                    height={boardH}
                    range={range}
                    bounds={bounds}
                    metrics={shownMetrics}
                    reached={reached}
                    onPick={onPick}
                    onStart={onStart}
                    startLabel={startLabel}
                    startHint={startHint}
                    searchHost={searchHost}
                    hues={hues}
                    groups={groups}
                    onShowAll={goTo}
                    aside={views}
                  />
                ) : (
                  <FlowBoard
                    facts={facts}
                    steps={steps}
                    width={boardW}
                    height={boardH}
                    range={range}
                    metrics={shownMetrics}
                    reached={reached}
                    onPick={onPick}
                    onStart={onStart}
                    startLabel={startLabel}
                    startHint={startHint}
                    searchHost={searchHost}
                    hues={hues}
                    groups={groups}
                    swaps={groups ? SWAPS : null}
                    onSwap={onSwap}
                    aside={views}
                  />
                )}
              </div>
            </div>
            {onOverview ? null : (
              <div style={{ padding: `${HEAD}px ${MARGIN}px 48px` }} data-fleet-part={active}>
                <FleetInventory kinds={shownKinds} scope={scope} width={boardW} root={scroller} hold={read.status === "loading"} onDrill={onDrill} sectionRef={noSectionRef} bounds={bounds} hues={hues} range={range} />
              </div>
            )}
          </>
        ) : null}
      </ScrollArea>
      {foot ? createPortal(parts, foot) : (
        <div className="flex shrink-0 justify-center border-t border-[color:var(--line-soft)] bg-[color:var(--bg)]" style={{ height: BAND_H }}>{parts}</div>
      )}
    </div>
  );
}
